import { useCallback, useEffect, useRef, useState } from "react";

const MIN = 1;
const MAX = 4.5;
const STEP = 1.12;

/**
 * Ctrl/Cmd + scroll over media → zoom (page zoom blocked).
 * Also works with left mouse held + scroll.
 */
export function useLightboxZoom(active: boolean) {
  const stageEl = useRef<HTMLDivElement | null>(null);
  const leftHeld = useRef(false);
  const [scale, setScale] = useState(1);

  const resetZoom = useCallback(() => setScale(1), []);

  const stageRef = useCallback((node: HTMLDivElement | null) => {
    stageEl.current = node;
  }, []);

  useEffect(() => {
    if (!active) {
      leftHeld.current = false;
      setScale(1);
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button === 0) leftHeld.current = true;
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.button === 0) leftHeld.current = false;
    };
    const onPointerCancel = () => {
      leftHeld.current = false;
    };

    const onWheel = (e: WheelEvent) => {
      const stage = stageEl.current;
      if (!stage) return;

      const overStage =
        stage.contains(e.target as Node) ||
        (() => {
          const r = stage.getBoundingClientRect();
          return (
            e.clientX >= r.left &&
            e.clientX <= r.right &&
            e.clientY >= r.top &&
            e.clientY <= r.bottom
          );
        })();

      if (!overStage) return;

      const withMod = e.ctrlKey || e.metaKey;
      if (!withMod && !leftHeld.current) return;

      e.preventDefault();
      e.stopPropagation();

      const dir = e.deltaY < 0 ? STEP : 1 / STEP;
      setScale((s) =>
        Math.min(MAX, Math.max(MIN, Number((s * dir).toFixed(3)))),
      );
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    // non-passive so we can block browser page-zoom
    window.addEventListener("wheel", onWheel, { passive: false, capture: true });

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("wheel", onWheel, true);
    };
  }, [active]);

  return { stageRef, scale, resetZoom };
}
